type GtagCommand = "config" | "event" | "consent" | "js" | "set";
type Gtag = (command: GtagCommand, ...args: unknown[]) => void;

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: Gtag;
  }
}

function getMeasurementId(): string {
  return (import.meta.env.VITE_GA_MEASUREMENT_ID as string) ?? "";
}

let consentGranted = false;
let scriptLoaded = false;

function ensureGtag(): void {
  if (scriptLoaded) return;
  scriptLoaded = true;

  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    window.dataLayer.push(arguments);
  };

  window.gtag("consent", "default", {
    analytics_storage: "denied",
  });

  window.gtag("js", new Date());

  const id = getMeasurementId();
  window.gtag("config", id, { send_page_view: false });

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${id}`;

  const append = () => document.head.appendChild(script);
  if (typeof requestIdleCallback === "function") {
    requestIdleCallback(append);
  } else {
    setTimeout(append, 1);
  }
}

export function grantConsent(): void {
  if (!getMeasurementId()) return;
  ensureGtag();
  consentGranted = true;
  window.gtag("consent", "update", { analytics_storage: "granted" });
}

export function denyConsent(): void {
  if (!getMeasurementId()) return;
  consentGranted = false;
  if (scriptLoaded) {
    window.gtag("consent", "update", { analytics_storage: "denied" });
  }
}

export function pageView(path: string, title?: string): void {
  if (!getMeasurementId() || !consentGranted) return;
  window.gtag("event", "page_view", {
    page_path: path,
    ...(title ? { page_title: title } : {}),
  });
}

export function event(name: string, params?: Record<string, string | number>): void {
  if (!getMeasurementId() || !consentGranted) return;
  window.gtag("event", name, params);
}

export function initFromStoredConsent(): void {
  if (!getMeasurementId()) return;
  try {
    const raw = localStorage.getItem("daynav.gdpr");
    if (!raw) return;
    const parsed = JSON.parse(raw);
    if (parsed.decision === "accept") grantConsent();
  } catch {
    // ignore parse errors
  }
}

export function _resetForTests(): void {
  consentGranted = false;
  scriptLoaded = false;
}
